class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch('/cart?section_id=main-cart-items')
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace = 
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = 
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

// Comprehensive Safari Back-Forward Cache Fix
// This handles the issue where Safari's bfcache causes cart items to disappear
// when using the browser back button after adding items to cart

// Create a centralized cart refresh function
function refreshCartState(source = 'unknown') {
  // console.log(`[Cart Refresh] Refreshing cart state from: ${source}`);
  
  // Use a more reliable approach: fetch cart data directly and update all cart elements
  fetch('/cart.js')
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((cartData) => {
      // console.log(`[Cart Refresh] Cart data received:`, cartData);
      
      // Update cart icon bubble count
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        // Update the hdr-cart-count-numb element (this is the actual count display)
        const cartCount = cartIconBubble.querySelector('.hdr-cart-count-numb');
        if (cartCount) {
          cartCount.textContent = cartData.item_count;
          cartCount.style.display = cartData.item_count > 0 ? 'block' : 'none';
        }
        
        // Also check for cart-count-bubble as fallback
        const cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
        if (cartCountBubble) {
          cartCountBubble.textContent = cartData.item_count;
          cartCountBubble.style.display = cartData.item_count > 0 ? 'block' : 'none';
        }
      }
      
      // Also update any standalone hdr-cart-count-numb elements
      const headerCartCounts = document.querySelectorAll('.hdr-cart-count-numb');
      headerCartCounts.forEach(cartCount => {
        cartCount.textContent = cartData.item_count;
        cartCount.style.display = cartData.item_count > 0 ? 'block' : 'none';
      });
      
      // Fallback: Try to refresh the cart icon bubble section
      fetch('/cart?section_id=cart-icon-bubble')
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const cartIconBubbleSource = html.querySelector('#cart-icon-bubble');
          if (cartIconBubbleSource) {
            const cartIconBubbleElement = document.getElementById('cart-icon-bubble');
            if (cartIconBubbleElement) {
              cartIconBubbleElement.innerHTML = cartIconBubbleSource.innerHTML;
            }
          }
        })
        .catch(e => {
          console.error('Error refreshing cart icon bubble section:', e);
        });
      
      // Update cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Toggle empty state
        cartDrawer.classList.toggle('is-empty', cartData.item_count === 0);
        
        // Fetch and update cart drawer content directly
        fetch('/cart?section_id=cart-drawer')
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const cartDrawerSource = html.querySelector('#CartDrawer');
            if (cartDrawerSource) {
              const cartDrawerElement = document.getElementById('CartDrawer');
              if (cartDrawerElement) {
                cartDrawerElement.innerHTML = cartDrawerSource.innerHTML;
              }
            }
          })
          .catch(e => {
            console.error('Error updating cart drawer content:', e);
            // Fallback: try to refresh using the existing cart update method
            const cartDrawerItems = document.querySelector('cart-drawer-items');
            if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
              cartDrawerItems.onCartUpdate();
            }
          });
      }
      
      // Update main cart items if they exist
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        cartItems.classList.toggle('is-empty', cartData.item_count === 0);
        if (typeof cartItems.onCartUpdate === 'function') {
          cartItems.onCartUpdate();
        }
      }
      
      // Publish cart update event to notify other components
      if (window.publish && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: `safari-bfcache-fix-${source}`,
          cartData: cartData 
        });
      }
      
    })
    .catch(e => {
      console.error('Error refreshing cart from bfcache:', e);
      
      // Fallback: try to refresh using the existing cart update methods
      const cartItems = document.querySelector('cart-items');
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      
      if (cartItems && typeof cartItems.onCartUpdate === 'function') {
        // console.log('Attempting fallback cart refresh...');
        cartItems.onCartUpdate();
      }
      
      if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
        // console.log('Attempting fallback cart drawer refresh...');
        cartDrawerItems.onCartUpdate();
      }
    });
}

// Make the function globally available for debugging
window.refreshCartState = refreshCartState;

// Enhanced event listeners for more comprehensive coverage
// 1. Standard pageshow event (works for first navigation)
window.addEventListener('pageshow', function(event) {
  if (event.persisted) {
    // console.log('[Cart Refresh] Safari bfcache detected via pageshow event');
    refreshCartState('pageshow-persisted');
  }
});

// 2. Additional focus event listener (Safari specific workaround)
// This helps catch cases where pageshow doesn't fire on subsequent back navigations
window.addEventListener('focus', function(event) {
  // Only run on Safari and only if we're coming from bfcache
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  if (isSafari) {
    // Check if the page was likely loaded from bfcache by examining performance navigation
    const navigation = performance.getEntriesByType('navigation')[0];
    
    // On Safari, when coming from bfcache, the navigation type might be 'back_forward'
    // or the transferSize might be 0 (indicating cached content)
    if (navigation && 
        (navigation.type === 'back_forward' || 
         (navigation.transferSize === 0 && navigation.duration < 50))) {
      // console.log('[Cart Refresh] Safari bfcache detected via focus event');
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        refreshCartState('focus-bfcache');
      }, 100);
    }
  }
});

// 3. Visibility change event (additional safety net)
// This catches cases where the tab becomes visible again after navigation
let lastVisibilityChange = 0;
document.addEventListener('visibilitychange', function() {
  if (!document.hidden) {
    const now = Date.now();
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Only run on Safari and throttle to avoid excessive calls
    if (isSafari && (now - lastVisibilityChange) > 1000) {
      lastVisibilityChange = now;
      
      // Check if this might be from a back navigation
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation && navigation.type === 'back_forward') {
        // console.log('[Cart Refresh] Safari bfcache detected via visibilitychange event');
        setTimeout(() => {
          refreshCartState('visibilitychange-bfcache');
        }, 150);
      }
    }
  }
});

// 4. Popstate event (for back/forward navigation detection)
// This is more reliable for detecting actual back/forward navigations
let isPopstateNavigation = false;
window.addEventListener('popstate', function(event) {
  // console.log('[Cart Refresh] Popstate event detected');
  isPopstateNavigation = true;
  
  // Clear the flag after a short delay
  setTimeout(() => {
    isPopstateNavigation = false;
  }, 2000);
  
  // On Safari, refresh cart state when back/forward navigation occurs
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari) {
    // console.log('[Cart Refresh] Safari back/forward navigation detected');
    setTimeout(() => {
      refreshCartState('popstate-navigation');
    }, 200);
  }
});

// 5. Additional DOMContentLoaded check for Safari
// Sometimes the cart state needs to be refreshed even on initial page load
document.addEventListener('DOMContentLoaded', function() {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  if (isSafari) {
    // Check if this page load might be from bfcache
    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation && 
        (navigation.type === 'back_forward' || 
         (navigation.transferSize === 0 && navigation.duration < 100))) {
      // console.log('[Cart Refresh] Safari bfcache detected on DOMContentLoaded');
      setTimeout(() => {
        refreshCartState('domcontentloaded-bfcache');
      }, 300);
    }
  }
});

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
